<template>
  <transition-group
    data-qa="spilka-formio"
    f-version="mf-0"
    name="s-zoom-in-center"
    tag="div">
    <div
      v-if="!preparedForm"
      key="load-formio"
      class="text-center">
      <sIcon
        class="my-auto mr-2"
        color="var(--color-main-green)"
        size="24"
        spin
        type="loading"/>
      <div
        class="my-auto"
        data-qa="formio-loading">
        {{ $t('Common.Loading') }}
      </div>
    </div>
    <SpilkaFormioMF
      v-else
      key="formio"
      ref="mf"
      :disabled="disabled"
      :form="preparedForm"
      :hide-submit="hideSubmit"
      :options="options"
      :show-alert="showAlert"
      @change="onChange"
      @customEvent="$emit('customEvent', $event)"
      @error="$emit('error', $event)"
      @formLoad="$emit('formLoad', $event)"
      @initialized="$emit('initialized', $event)"
      @redraw="$emit('redraw', $event)"
      @render="$emit('render', $event)"
      @submit-error="$emit('submit-error', $event)"
      @submit-valid="submitValid"
      @validate="$emit('validate', $event)">
      <slot/>
    </SpilkaFormioMF>
    <div
      v-if="submissionData"
      key="formValues"
      class="formio-values"
      data-qa="formio-values">
      <template
        v-for="formValue in Object.entries(submissionData)">
        <div
          :key="formValue[0]"
          :data-qa="formValue[0]">
          {{ formValue[1] }}
        </div>
      </template>
    </div>
  </transition-group>
</template>
<script>
/**
 * Компонент для работы с formio (legacy).
 *
 * фасад-адаптер над SpilkaFormioMF (микрофронтенд):
 *  - грузит форму «старым» эндпоинтом $_getForm (с произвольными params);
 *  - конвертирует ответ в формат MF (toMfForm);
 *  - делегирует рендер, события и ref-методы в SpilkaFormioMF.
 *
 * Публичный контракт (props, события, ref-методы) сохранён, поэтому
 * потребители (ConfirmTenderAgreement, RejectDialog, ListOfAcceptors)
 * менять не нужно.
 */

import formio from 'mixins/formio'
import { storageMixin } from 'mixins'
import SpilkaFormioMF from 'components/common/SpilkaFormioMF.vue'
import { toMfForm } from './formioLegacyAdapter'

export default {
  name: 'spilkaFormioMFOld',
  components: {
    SpilkaFormioMF,
  },
  mixins: [storageMixin, formio],
  data() {
    return {
      preparedForm: null,
      components: [],
      submissionData: {},
      formValues: {},
    }
  },
  methods: {
    buildPreparedForm() {
      this.preparedForm = toMfForm(this.components, this.submissionData)
      this.$nextTick(() => {
        if (this.$refs.mf) {
          this.$refs.mf.formRendered = true
        }
      })
    },
    async applyDraftToSubmission() {
      if (!this.keyDraft) {
        return
      }
      const draft = await this.$_getPropFromDBStorage(this.keyDraft, null, false)
      if (draft) {
        this.submissionData = { ...draft }
      }
    },
    async loadData() {
      if (!this.formParams) {
        this.$notice.error({
          title: this.$t('Common.ErrorTitle'),
          dangerouslyUseHTMLString: true,
          message: this.$t('Common.IncorrectParametersEmptyValues'),
          showClose: true,
          progress: true,
        })
        this.components = []
        this.buildPreparedForm()
        return
      }
      if (!this.formParams.formCode) {
        this.$notice.error({
          title: this.$t('Common.ErrorTitle'),
          dangerouslyUseHTMLString: true,
          message: this.$t('Common.IncorrectParametersCodeMandatory'),
          showClose: true,
          progress: true,
        })
        this.components = []
        this.buildPreparedForm()
        return
      }
      const legacy = await this.$_getForm(this.formParams)
      await this.applyDraftToSubmission()
      this.components = legacy.components || []
      this.buildPreparedForm()
    },
    onChange(payload) {
      const data = payload?.data || {}
      this.formValues = data
      this.submissionData = { ...this.submissionData, ...data }
      this.$emit('change', payload)
    },
    async saveDraft() {
      if (!this.keyDraft) {
        return
      }
      await this.$_savePropToDBStorage(this.keyDraft, this.formValues)
    },
    setValueToItem(itemKey, itemValue) {
      if (this.$_.isEqual(this.submissionData[itemKey], itemValue)) {
        return
      }
      this.$set(this.submissionData, itemKey, itemValue)
      this.buildPreparedForm()
    },
    submitValid(objectValues) {
      const getValue = (value) => {
        if (!value) {
          return ''
        }
        return typeof value === 'string' ? value : JSON.stringify(value)
      }

      // formio-компонент "container" в MF-форме группирует поля во вложенный
      // объект, тогда как старая форма отдаёт их как плоский список — разворачиваем
      const flatValues = objectValues.flatMap((objectValue) => {
        if (objectValue.key !== 'container' || !objectValue.value || typeof objectValue.value !== 'object') {
          return [objectValue]
        }
        return Object.entries(objectValue.value).map(([key, value]) => ({ key, value }))
      })

      const resultKeyValue = flatValues.map((objectValue) => {
        return {
          key: objectValue.key?.toString() ?? '',
          value: getValue(objectValue.value),
        }
      })

      this.$emit('submit-valid', resultKeyValue)
    },
    submit() {
      return this.$refs.mf?.submit()
    },
    checkValidity() {
      return this.$refs.mf?.validate()
    },
    validate() {
      return this.$refs.mf?.validate()
    },
    resetForm() {
      return this.$refs.mf?.resetForm()
    },
    getSubmission() {
      return this.$refs.mf?.getSubmission()
    },
  },
  async mounted() {
    if (!this.form && this.formParams) {
      await this.loadData()
    }
  },
  watch: {
    form: {
      immediate: true,
      deep: true,
      handler(newValue) {
        if (!newValue) {
          return
        }
        this.components = newValue.components || []
        this.buildPreparedForm()
      },
    },
  },
  props: {
    /**
     * JSON directly
     */
    form: {
      type: Object,
    },
    /**
     * ключ для черновика. Если не передать, функционал черновика работать не будет
     */
    keyDraft: {
      type: String,
    },
    /**
     * Параметры формы из RMD
     * {
     * formCode: String // код формы
     * params: Array // массив параметров {key, value}
     * }
     */
    formParams: {
      type: Object,
    },
    options: {
      type: Object,
    },
    /**
     * Показывать блок со списком ошибок
     */
    showAlert: {
      type: Boolean,
      default: true,
    },
    hideSubmit: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
    },
  },
}
</script>
<style lang="scss" scoped>
.formio-values {
  display: none;
}
</style>
